/**
 * Positions -- Loan Participations FI-tenant active participation list.
 *
 * Sprint C14 Lane B WP B-7.
 *
 * Lists the caller FI's activated participation positions via
 * `GET /api/v1/syndication/positions`. The endpoint is caller-scoped
 * (the role-gate splits per A-5 ensure FI-tenant operators see only
 * their own positions). A click-through routes to PositionDetail
 * for the position-level distribution history.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataGrid,
  Skeleton,
  Card,
  Badge,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Button,
  FilterBar,
} from '@aaimplatform/ui';
import type { ColumnDef, FilterDefinition, ActiveFilter } from '@aaimplatform/ui';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import { loanSyndicationClient } from '../../api/client';
import {
  formatCurrencyFull,
  formatPercent,
  formatRatioPercent,
  formatCurrencyCompact as formatCurrency,
  formatDate,
  POSITION_STATUS_LABELS,
  PARTICIPATION_ROLE_LABELS,
} from './format';
import { toSyndicationReadError } from './read-error';
import { ReadErrorBanner } from './ReadErrorBanner';

type ParticipationPosition = LspComponents['schemas']['ParticipationPosition'];
type PositionStatus = ParticipationPosition['status'];
type PositionRole = ParticipationPosition['role'];

/**
 * Commitment-weighted annualized yield over the positions that report one.
 *
 * A position with no annualizedYield is left out of the numerator AND the
 * denominator: counting it as 0% would dilute the figure with a yield nobody
 * measured. With no reporting position there is no yield to state, so the
 * result is null (the card prints '--'), never a measured-looking 0.00%.
 */
export function weightedAnnualizedYield(
  positions: ReadonlyArray<Pick<ParticipationPosition, 'committedAmount' | 'annualizedYield'>>,
): number | null {
  let principal = 0;
  let weighted = 0;
  for (const p of positions) {
    if (typeof p.annualizedYield !== 'number') continue;
    principal += p.committedAmount;
    weighted += p.committedAmount * p.annualizedYield;
  }
  return principal === 0 ? null : weighted / principal;
}

function getPositionStatusVariant(
  status: PositionStatus,
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'MATURED':
      return 'neutral';
    case 'DEFAULTED':
      return 'error';
    case 'EXITED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'MATURED', label: 'Matured' },
      { value: 'DEFAULTED', label: 'Defaulted' },
      { value: 'EXITED', label: 'Exited' },
    ],
  },
  {
    id: 'role',
    label: 'Role',
    type: 'select',
    options: [
      { value: 'LEAD', label: 'Lead' },
      { value: 'PARTICIPANT', label: 'Participant' },
    ],
  },
];

function usePositions(
  status: PositionStatus | undefined,
  role: PositionRole | undefined,
) {
  return useQuery({
    queryKey: ['lsp-positions', status ?? 'ALL', role ?? 'ALL'],
    queryFn: async () => {
      const { data, error, response } = await loanSyndicationClient.GET(
        '/api/v1/syndication/positions',
        {
          params: {
            query: {
              status,
              role,
              page: 1,
              pageSize: 100,
            },
          },
        },
      );
      // Read before the error narrowing: on a route whose contract declares
      // no error body, openapi-fetch types that branch as never.
      const httpStatus = response.status;
      if (error) {
        throw toSyndicationReadError(error, httpStatus, 'Failed to load positions');
      }
      return data;
    },
    staleTime: 30 * 1000,
    retry: false,
  });
}

export function Positions() {
  const navigate = useNavigate();
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [search, setSearch] = useState('');

  const statusFilter = activeFilters.find((f) => f.filterId === 'status')?.value as
    | PositionStatus
    | undefined;
  const roleFilter = activeFilters.find((f) => f.filterId === 'role')?.value as
    | PositionRole
    | undefined;

  const { data, isLoading, error } = usePositions(statusFilter, roleFilter);

  const positions = data?.positions ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return positions;
    const q = search.toLowerCase();
    return positions.filter(
      (p) =>
        p.positionId.toLowerCase().includes(q) ||
        p.offeringId.toLowerCase().includes(q) ||
        (p.institutionName ?? '').toLowerCase().includes(q),
    );
  }, [positions, search]);

  // committedAmount and totalInterestReceived are required by the
  // ParticipationPosition contract, so the sums need no default.
  const totalCommitted = useMemo(
    () => filtered.reduce((sum, p) => sum + p.committedAmount, 0),
    [filtered],
  );
  const totalInterest = useMemo(
    () => filtered.reduce((sum, p) => sum + p.totalInterestReceived, 0),
    [filtered],
  );
  const weightedYield = useMemo(() => weightedAnnualizedYield(filtered), [filtered]);
  const filtersActive = activeFilters.length > 0 || search.trim().length > 0;
  const activeCount = useMemo(
    () => filtered.filter((p) => p.status === 'ACTIVE').length,
    [filtered],
  );

  const columns = useMemo<ColumnDef<ParticipationPosition, unknown>[]>(
    () => [
      {
        id: 'position',
        header: 'Position',
        cell: ({ row }) => (
          <button
            type="button"
            className="text-sm font-mono text-accent hover:text-accent/80 hover:underline tabular-nums leading-normal text-left"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/syndication/positions/${row.original.positionId}`);
            }}
          >
            {row.original.positionId.slice(0, 8)}...
          </button>
        ),
        size: 130,
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ row }) => (
          <Badge
            variant={row.original.role === 'LEAD' ? 'info' : 'neutral'}
            size="sm"
          >
            {PARTICIPATION_ROLE_LABELS[row.original.role] ?? row.original.role}
          </Badge>
        ),
        size: 120,
      },
      {
        accessorKey: 'committedAmount',
        header: 'Committed',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 160,
      },
      {
        accessorKey: 'participationShare',
        header: 'Share',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatRatioPercent(getValue() as number)}
          </span>
        ),
        size: 100,
      },
      {
        accessorKey: 'totalInterestReceived',
        header: 'Interest earned',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 160,
      },
      {
        accessorKey: 'annualizedYield',
        header: 'Yield',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatPercent(getValue() as number | undefined)}
          </span>
        ),
        size: 100,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={getPositionStatusVariant(row.original.status)} size="sm">
            {POSITION_STATUS_LABELS[row.original.status] ?? row.original.status}
          </Badge>
        ),
        size: 130,
      },
      {
        accessorKey: 'nextDistributionDate',
        header: 'Next distribution',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDate(getValue() as string | undefined)}
          </span>
        ),
        size: 160,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              navigate(`/syndication/positions/${row.original.positionId}`)
            }
          >
            View
          </Button>
        ),
        size: 100,
      },
    ],
    [navigate],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            Active and historical participation positions across the Loan
            Participations network.
          </p>
        </div>
      </div>

      {error && (
        <ReadErrorBanner
          error={error}
          subject="positions"
          surface="Loan Participations -- Positions"
        />
      )}

      <MetricCardGrid className="mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))
        ) : error ? (
          // A failed read has no totals. The tiles stay so the page keeps its
          // shape, but each value is withheld and says why, never a measured
          // 0 / $0 / 0.00% over an empty list.
          <>
            <MetricCard label="Active Positions" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Committed" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Interest Earned" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Weighted Yield" value="--" caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          <>
            <MetricCard
              label="Active Positions"
              value={activeCount.toString()}
              change={`${filtered.length} total visible`}
              changeType="neutral"
            />
            <MetricCard
              label="Committed"
              value={formatCurrency(totalCommitted)}
              change="Across all positions"
              changeType="neutral"
            />
            <MetricCard
              label="Interest Earned"
              value={formatCurrency(totalInterest)}
              change="Cumulative"
              changeType="neutral"
            />
            <MetricCard
              label="Weighted Yield"
              value={formatPercent(weightedYield)}
              change={weightedYield === null ? 'No position reports a yield' : 'Annualized'}
              changeType="neutral"
            />
          </>
        )}
      </MetricCardGrid>

      {!error && (
        <FilterBar
          filters={FILTER_DEFINITIONS}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by position id or offering id..."
          className="rounded-t-sm"
        />
      )}

      {isLoading ? (
        <Card className="p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </Card>
      ) : error ? null : filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-sm font-sans text-secondary">
            {filtersActive
              ? 'No positions match the current filters.'
              : 'No positions yet. A position opens when a commitment activates.'}
          </div>
        </Card>
      ) : (
        <DataGrid<ParticipationPosition>
          data={filtered}
          columns={columns}
          enablePagination
          defaultPageSize={20}
          enableColumnResize
          hoverable
          getRowId={(row) => row.positionId}
          className="w-full rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
